import { createContext, useContext, type RefCallback } from 'react';
import type { WatchBinding } from '../screens/watch/useWatchScreen';

/**
 * How the watch page reaches a player it no longer owns.
 *
 * The binding used to be created by the routed screen and handed down as a prop. It cannot be any
 * more: the player has to outlive the route, so it is created above the router
 * (`PlaybackHost.tsx`) and the page that happens to be showing it reads it from here.
 *
 * Its own module so nothing imports in a circle — `PlaybackHost` renders `PlayerSurface` out of
 * `screens/watch/view.tsx`, and that file needs the slot from this side of the wall.
 */
export interface PlaybackContextValue {
  readonly binding: WatchBinding;
  /** A callback ref: the watch page's stage registers itself, and `null` on the way out. */
  readonly slotRef: RefCallback<HTMLElement>;
}

const PlaybackContext = createContext<PlaybackContextValue | null>(null);

export const PlaybackProvider = PlaybackContext.Provider;

/**
 * `null` when nothing is loaded — which on a watch route cannot happen, because the host derives the
 * session from the route during render rather than in an effect. The type keeps the caller honest
 * about the one frame between "no session" and "the first one".
 */
export function usePlayback(): PlaybackContextValue | null {
  return useContext(PlaybackContext);
}

/** Registers the watch page's stage as the player's home while that page is mounted. */
export function usePlayerSlot(): RefCallback<HTMLElement> {
  return usePlayback()?.slotRef ?? NO_SLOT;
}

const NO_SLOT: RefCallback<HTMLElement> = () => undefined;
